import { Student } from "../providers/studentsProvider";
import { DateTime } from "luxon";
import { useGetList } from "react-admin";
import { DrivingLesson } from "../model/autovioCalendarEvents";
import { DrivingLicenseClass } from "../model/DrivingLicenseClass";
import { DrivingLessonType } from "../model/DrivingLessonType";
import { SpecialDrivingLessonProductType } from "../model/ProductType";
import { durationInMinutes } from "../utils/calendar";

export function useInsufficientCompulsoryLessonsWarning(formValues: {
  examType: "theoretischePruefung" | "praktischePruefung" | "" | null | undefined;
  student: Student | null | undefined;
  drivingLicenseClass: DrivingLicenseClass | "" | null | undefined;
  dateTime: DateTime | null | undefined;
}): string | null {
  const { examType, student, drivingLicenseClass, dateTime } = formValues;
  const bookedTraining = student?.bookedTrainings.find(
    (it) =>
      !it.isFinished &&
      !it.drivingSchoolChange &&
      it.includesPracticalExam &&
      it.drivingLicenseClass === drivingLicenseClass,
  );
  const compulsorySpecialDrivingLessons = bookedTraining?.compulsorySpecialDrivingLessons;
  const { data: specialDrivingLessons } = useGetList<DrivingLesson>(
    "drivingLessons",
    {
      filter: {
        drivingSchoolId: student?.drivingSchoolId,
        studentId: student?.id,
        drivingLessonType: ["autobahnfahrt", "nachtfahrt", "ueberlandfahrt"],
      },
      sort: { field: "start", order: "DESC" },
      pagination: { page: 1, perPage: 999 },
    },
    { enabled: !!(examType === "praktischePruefung" && student && compulsorySpecialDrivingLessons) },
  );
  const a: string[] = [];
  if (
    examType === "praktischePruefung" &&
    student &&
    bookedTraining &&
    specialDrivingLessons &&
    compulsorySpecialDrivingLessons &&
    dateTime?.isValid
  ) {
    for (const [drivingLessonType, productType] of Object.entries(_specialDrivingLessonTypes)) {
      const expected = compulsorySpecialDrivingLessons[productType];
      if (expected) {
        const actual = specialDrivingLessons
          .filter(
            (it) =>
              it.drivingLessonType === drivingLessonType &&
              it.student.bookedTrainingId === bookedTraining.id &&
              it.start < dateTime &&
              (it.student.rsvp === "pending" || it.student.rsvp === "accepted"),
          )
          .reduce((acc, it) => acc + durationInMinutes(it) / 45, 0);
        const delta = Math.ceil(expected - actual);
        if (delta > 0) {
          a.push(delta === 1 ? `1 ${productType}` : `${delta} ${productType}en`);
        }
      }
    }
    if (a.length > 0) {
      const prefix = `${student.callName} benötigt noch `;
      if (a.length === 1) {
        return prefix + a[0];
      } else if (a.length === 2) {
        return prefix + a[0] + " und " + a[1];
      } else if (a.length === 3) {
        return prefix + a[0] + ", " + a[1] + " und " + a[2];
      }
    }
  }
  return null;
}

const _specialDrivingLessonTypes: Partial<Record<DrivingLessonType, SpecialDrivingLessonProductType>> = {
  autobahnfahrt: "Autobahnfahrt",
  nachtfahrt: "Nachtfahrt",
  ueberlandfahrt: "Überlandfahrt",
};
